import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router";
import { TopNav } from "./TopNav";
import { UtilityBar } from "./UtilityBar";
import { Sidebar } from "./Sidebar";
import { CommandPalette } from "../shared/CommandPalette";
import { ToastContainer } from "../shared/Toast";
import { PrFab } from "../shared/PrFab";
import { useUiStore } from "../../stores/useUiStore";

interface ShellProps {
  children: ReactNode;
  /** When false, the Sidebar is suppressed (it fires API calls on mount,
   *  so it must not render until Clerk auth + org are settled). */
  authReady?: boolean;
}

export function Shell({ children, authReady = true }: ShellProps) {
  const { pathname } = useLocation();
  const wide =
    pathname.includes("/suggestions") || pathname.startsWith("/doc/");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div style={styles.shell}>
      <TopNav />
      <UtilityBar onSearch={() => setPaletteOpen(true)} />
      <div style={styles.body}>
        {sidebarOpen && authReady && <Sidebar />}
        <main style={styles.main}>
          <div style={{ ...styles.inner, ...(wide ? styles.innerWide : {}) }}>
            {children}
          </div>
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
      <ToastContainer />
      {authReady && <PrFab />}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    background: "var(--comp-canvas)",
  },
  body: {
    display: "flex",
    flex: 1,
    alignItems: "stretch",
  },
  main: {
    flex: 1,
    minWidth: 0,
    background: "var(--comp-paper)",
  },
  inner: {
    width: "100%",
    maxWidth: 1040,
    margin: "0 auto",
  },
  innerWide: {
    maxWidth: "none",
  },
};
